import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { IMediaItem, IMediaVariantMetadata, ISiteDetails } from "../../../models/Interfaces";
import { MediaService } from "../../../services/MediaService";
import { SiteService } from "../../../services/SiteService";
import { Button, Card, CardBody, CardFooter, Col, InputGroup, Modal, ModalBody, ModalHeader, Row, Spinner } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faDownload, faPen, faFileImage, faFolderOpen, faImage, faMagicWandSparkles, faMagnifyingGlass, faTrash, faUpload } from "@fortawesome/free-solid-svg-icons";
import { faImage as faImageRegular } from "@fortawesome/free-regular-svg-icons";
import InputGroupText from "react-bootstrap/esm/InputGroupText";
import ImageEditor from "../../components/common/ImageEditor";
import { FileTypeHelper } from "../../../helpers/FileTypeHelper";
import { useConfirm } from "../../../config/ConfirmProvider";
import { createRoot } from "react-dom/client";
import { actionEmitter, EmitEvent } from "../../../config/ActionEmitter";
import AppEmptyState from "../../components/common/AppEmptyState";
import Loading from "../../components/common/Loading";
import { canEditAiImage, canGenerateAiImage } from "../../../AIHelper/aiImageClient";
import RenameMediaDialog from "./RenameMediaDialog";
import AiImageDialog, { AiImageDialogMode } from "./AiImageDialog";
import { mediaForFolder } from "./folderMedia";
import { isPreviewableMedia } from "./mediaPreview";
import styles from "./mediaUploadPreview.module.scss";

const PreviewPlaceholder = (): JSX.Element => (
  <span className={`text-secondary ${styles.previewPlaceholder}`} title="Preview not available">
    <FontAwesomeIcon icon={faImageRegular} size="5x" />
  </span>
);

const BrowserImagePreview = ({ url, alt }: { url: string; alt: string }): JSX.Element => {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  if (!url || failedUrl === url) {
    return <PreviewPlaceholder />;
  }

  return (
    <img
      src={url}
      alt={alt}
      className={styles.previewImage}
      onError={() => setFailedUrl(url)}
    />
  );
};

interface IMediaUploadPreviewProps {
  mediafiles: Array<IMediaItem>;
  folderId: number;
  folderName?: string;
  loading?: boolean;
  onLibraryChange?: (files: Array<IMediaItem>) => void;
}

export interface IMediaUploadPreviewRef {
  setPreviewFile: (fileid: number, folderid: number) => void;
}

const MediaUploadPreview = forwardRef<IMediaUploadPreviewRef, IMediaUploadPreviewProps>(({ mediafiles, folderId, folderName = "this folder", loading = false, onLibraryChange }, ref) => {
  const [existingMedia, setExistingMedia] = useState<Array<IMediaItem>>(() => mediaForFolder(mediafiles, folderId));
  const [seenFolderId, setSeenFolderId] = useState(folderId);
  const [seenMediaFiles, setSeenMediaFiles] = useState(mediafiles);
  const [newMedia, setNewMedia] = useState<Array<IMediaItem>>([]);
  const [previewFile, setPreviewFile] = useState<IMediaItem | null>(null);
  const [previewEditorUrl, setPreviewEditorUrl] = useState<string | null>(null);
  const [previewEditorError, setPreviewEditorError] = useState<string | null>(null);
  const [variantPreviewFile, setVariantPreviewFile] = useState<IMediaItem | null>(null);
  const [fullScreenEditor, setFullScreenEditor] = useState<boolean>(false);
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());
  const deletingIdsRef = useRef(new Set<number>());
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadingFileName, setUploadingFileName] = useState<string | null>(null);
  const [siteDetails, setSiteDetails] = useState<ISiteDetails | null>(null);
  const [aiDialog, setAiDialog] = useState<{ mode: AiImageDialogMode; source: IMediaItem | null } | null>(null);
  const [renameFile, setRenameFile] = useState<IMediaItem | null>(null);
  const [mediaRevisions, setMediaRevisions] = useState<Record<number, number>>({});

  const mediaSvcRef = useRef<MediaService | null>(null);
  const folderIdRef = useRef(folderId);
  const notifyLibraryRef = useRef(false);
  folderIdRef.current = folderId;

  const publishLibrary = (files: Array<IMediaItem>): void => {
    notifyLibraryRef.current = true;
    setExistingMedia(files);
  };
  const confirmation = useConfirm();

  if (seenMediaFiles !== mediafiles || seenFolderId !== folderId) {
    const folderChanged = seenFolderId !== folderId;
    setSeenMediaFiles(mediafiles);
    setSeenFolderId(folderId);
    setExistingMedia(mediaForFolder(mediafiles, folderId));

    if (folderChanged) {
      setNewMedia([]);
      setPreviewFile(null);
      setVariantPreviewFile(null);
      setAiDialog(null);
      setRenameFile(null);
      setPreviewEditorUrl(null);
      setPreviewEditorError(null);
    }
  }
  const aiGenerateAvailable = folderId > 0 && canGenerateAiImage(siteDetails);
  const aiEditAvailable = folderId > 0 && canEditAiImage(siteDetails);

  const getMetadata = (file: IMediaItem): IMediaVariantMetadata | null => {
    if (!file.MetadataJson) {
      return null;
    }

    try {
      return JSON.parse(file.MetadataJson) as IMediaVariantMetadata;
    } catch {
      return null;
    }
  };

  const getVariantFiles = (file: IMediaItem, mediaItems: Array<IMediaItem>): Array<IMediaItem> => {
    if (file.SrcSet && file.SrcSet.length > 0) {
      return file.SrcSet;
    }

    return mediaItems.filter((item) => item.SourceFileId === file.Id);
  };

  const getOriginalFiles = (mediaItems: Array<IMediaItem>): Array<IMediaItem> => {
    return mediaItems.filter((item) => !item.SourceFileId);
  };

  const variantWidth = (file: IMediaItem): number | null => {
    const metadata = getMetadata(file);
    if (typeof metadata?.width === "number" && metadata.width > 0) {
      return metadata.width;
    }

    const match = file.Filename.match(/(?:^|[_-])(\d+)w(?:\.[a-z0-9]+)?$/i);
    return match ? Number(match[1]) : null;
  };

  const getVariantLabel = (file: IMediaItem): string => {
    const width = variantWidth(file);
    if (width) {
      return `${width}w`;
    }

    const metadata = getMetadata(file);
    if (metadata?.label && metadata.label.length <= 24) {
      return metadata.label;
    }

    if (metadata?.height) {
      return `${metadata.height}px`;
    }

    return "Size";
  };

  const resolveMediaUrl = (url: string | null | undefined): string => {
    if (!url) {
      return "";
    }

    try {
      return new URL(url, window.location.origin).toString();
    } catch {
      return url;
    }
  };

  const withRevision = (url: string, fileId: number): string => {
    const revision = mediaRevisions[fileId];
    if (!revision || !url) {
      return url;
    }

    return `${url}${url.includes("?") ? "&" : "?"}v=${revision}`;
  };

  useImperativeHandle(ref, () => ({
    setPreviewFile: async (fileid: number, previewFolderId: number) => {
      if (mediaSvcRef.current) {
        const files = await mediaSvcRef.current.getFilesForFolder(previewFolderId);
        const file = files.find((current) => current.Id === fileid);

        if (file) {
          setPreviewFile(file);
        }
      }
    }
  }));

  const { getRootProps, getInputProps, isDragAccept, isDragReject } = useDropzone({
    onDrop: (acceptedFiles, fileRejections) => {
      console.log("Accepted files:", acceptedFiles.map((file) => ({
        name: file.name,
        type: file.type,
      })));
      console.log("Rejected files:", fileRejections.map((rejection) => ({
        name: rejection.file.name,
        type: rejection.file.type,
        errors: rejection.errors,
      })));

      const allowedExtensions = [
        ".jpg", ".jpeg", ".png", ".gif",
        ".pdf",
        ".mp4", ".mov", ".mpg", ".webm", ".mpeg",
        ".doc", ".docx",
        ".xls", ".xlsx",
        ".ppt", ".pptx",
        ".vsd", ".vsdx",
        ".gpx",
      ];
      const allowedMimePrefixes = [
        "image/",
        "application/pdf",
        "video/",
        "application/msword",
        "application/vnd.openxmlformats-officedocument",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "application/vnd.visio",
        "application/vnd.ms-visio.drawing",
        "application/gpx+xml",
        "application/xml",
        "text/xml",
      ];

      const validFiles = acceptedFiles.filter((file) => {
        const ext = file.name.toLowerCase();
        const hasValidExtension = allowedExtensions.some((allowedExt) => ext.endsWith(allowedExt));
        const hasValidMime = allowedMimePrefixes.some((prefix) => file.type.startsWith(prefix));
        return hasValidExtension || hasValidMime;
      });

      const items = validFiles.map((file) => {
        const ext = file.name.toLowerCase();
        return {
          SiteId: "",
          Id: 0,
          Filename: file.name,
          DateAdded: new Date().toISOString(),
          AddedBy: "",
          Url: URL.createObjectURL(file),
          FileType: FileTypeHelper.getFileTypeFromMimeType(file.type, file.name),
          FileData: file,
          ContentType: file.type || (ext.endsWith(".gpx") ? "application/gpx+xml" : file.type),
          FolderId: folderId,
          FolderName: "",
          FolderLevel: 0,
          SourceFileId: null,
          MetadataJson: null,
          SrcSet: [],
          IsVideo: file.type.startsWith("video/")
        };
      });

      if (items.length > 0) {
        setNewMedia((prev) => [...prev, ...items]);
      }
    },
  });

  const mediaItemDragStart = (event: React.DragEvent<HTMLElement>, mediaitem: IMediaItem): void => {
    const dragImage = document.createElement("div");
    dragImage.style.width = "60px";
    dragImage.style.height = "60px";
    dragImage.style.color = "grey";
    dragImage.style.position = "absolute";
    dragImage.style.top = "-1000px";

    document.body.appendChild(dragImage);
    const root = createRoot(dragImage);
    root.render(<FontAwesomeIcon icon={faImage} size="2x" />);

    event.dataTransfer.setDragImage(dragImage, 20, 20);
    event.dataTransfer.setData("DRAGGEDOBJECT", JSON.stringify(mediaitem));
    event.dataTransfer.setData("SOURCE", "external");

    const onDragEnd = () => {
      dragImage.remove();
      event.currentTarget?.removeEventListener("dragend", onDragEnd);
    };

    event.currentTarget?.addEventListener("dragend", onDragEnd);
  };

  const renderPreview = (file: IMediaItem) => {
    if (!isPreviewableMedia(file)) {
      return <PreviewPlaceholder />;
    }

    const mediaUrl = withRevision(resolveMediaUrl(file.Url), file.Id);

    switch (file.FileType.toLowerCase()) {
      case "image":
        return <BrowserImagePreview url={mediaUrl} alt={file.Filename} />;
      case "pdf":
        return <iframe src={mediaUrl} width="100%" height="100%" title="PDF Preview"></iframe>;
      case "office":
        return <iframe src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(mediaUrl)}`} width="100%" height="100%" title="Office File Preview"></iframe>;
      case "video":
        return (
          <video controls className={styles.previewImage}>
            <source src={mediaUrl} type={file.ContentType || "video/mp4"} />
            Your browser does not support the video tag.
          </video>
        );
      default:
        return <PreviewPlaceholder />;
    }
  };

  const handleSaveEditedImage = async (updatedImageBlob: Blob): Promise<boolean> => {
    if (!previewFile) {
      return false;
    }

    const type = updatedImageBlob.type || FileTypeHelper.getImageMimeType(previewFile.Filename);
    const file = new File([updatedImageBlob], previewFile.Filename, { type });

    if (!previewFile.Id) {
      const nextUrl = URL.createObjectURL(file);
      setNewMedia((prev) => prev.map((item) => {
        if (item.Filename !== previewFile.Filename || item.Id !== 0) {
          return item;
        }

        if (item.Url?.startsWith("blob:")) {
          URL.revokeObjectURL(item.Url);
        }

        return { ...item, FileData: file, Url: nextUrl, ContentType: type };
      }));
      actionEmitter.emit(EmitEvent.Result, true, `${previewFile.Filename} preview updated. Upload the file to store it.`);
      return true;
    }

    if (!mediaSvcRef.current) {
      return false;
    }

    const confirmed = await confirmation.requestConfirmation(
      `Replace <b>${previewFile.Filename}</b>? Pages using this address will show the new image, and its generated sizes will be refreshed.<br /><b>This action cannot be undone.</b>`,
      "Replace Media Item"
    );

    if (!confirmed) {
      return false;
    }

    const replaced = await mediaSvcRef.current.replaceFile(file, previewFile.Id);
    if (!replaced) {
      return false;
    }

    const files = mediaForFolder(await mediaSvcRef.current.getFilesForFolder(folderId), folderId);
    if (folderIdRef.current !== folderId) {
      return true;
    }

    const refreshed = files.find((item) => item.Id === replaced.Id) ?? replaced;
    if (files.length > 0) {
      publishLibrary(files);
    } else {
      const variants = Array.isArray(replaced.SrcSet) ? replaced.SrcSet : [];
      publishLibrary([...existingMedia.filter((item) => item.Id !== replaced.Id && item.SourceFileId !== replaced.Id), replaced, ...variants]);
    }

    const stamp = Date.now();
    setMediaRevisions((prev) => {
      const next = { ...prev, [refreshed.Id]: stamp };
      const sized = files.length > 0 ? files : (Array.isArray(replaced.SrcSet) ? replaced.SrcSet : []);
      sized.filter((item) => item.SourceFileId === refreshed.Id).forEach((item) => {
        next[item.Id] = stamp;
      });
      return next;
    });
    setPreviewFile(refreshed);
    mediaSvcRef.current.EmitDataSuccess(`${refreshed.Filename} replaced`);
    return true;
  };

  const handleCopyUrl = (mediaitem: IMediaItem): void => {
    const mediaUrl = resolveMediaUrl(mediaitem.Url);
    navigator.clipboard.writeText(mediaUrl);
    actionEmitter.emit(EmitEvent.Result, true, `URL for ${mediaitem.Filename} copied to clipboard`);
  };

  const handleDownloadFile = async (mediaitem: IMediaItem): Promise<void> => {
    try {
      const blob = await (mediaSvcRef.current ?? new MediaService()).downloadFileContent(mediaitem.Id);
      const blobUrl = URL.createObjectURL(blob);

      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = mediaitem.Filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading the file:", error);
    }
  };

  const handleUploadFile = async (mediaitem: IMediaItem): Promise<void> => {
    if (mediaSvcRef.current && mediaitem.FileData) {
      try {
        setUploading(true);
        setUploadingFileName(mediaitem.Filename);
        const res = await mediaSvcRef.current.uploadFile(mediaitem.FileData, folderId);
        if (folderIdRef.current !== folderId) {
          return;
        }

        if (res) {
          publishLibrary([...existingMedia, res]);
          setNewMedia((prev) => prev.filter((pending) => pending.Filename !== mediaitem.Filename));
        }
      } finally {
        setUploading(false);
        setUploadingFileName(null);
      }
    }
  };

  const handleUploadFiles = async (): Promise<void> => {
    if (mediaSvcRef.current && newMedia.length > 0) {
      const files = newMedia
        .filter((item): item is IMediaItem & { FileData: File } => item.FileData !== null && item.FileData !== undefined)
        .map((item) => item.FileData);

      if (files.length > 0) {
        try {
          setUploading(true);
          setUploadingFileName(null);
          const res = await mediaSvcRef.current.uploadFiles(files, folderId);
          if (folderIdRef.current !== folderId) {
            return;
          }

          if (res !== null && res.length > 0) {
            let updatedNewMedia = [...newMedia];
            let updatedExistingMedia = [...existingMedia];

            for (const uploaded of res) {
              updatedNewMedia = updatedNewMedia.filter((file) => file.Filename.toLowerCase() !== uploaded.Filename.toLowerCase());
              const index = updatedExistingMedia.findIndex((file) => file.Id === uploaded.Id);

              if (index !== -1) {
                updatedExistingMedia[index] = uploaded;
              } else {
                updatedExistingMedia.push(uploaded);
              }
            }

            setNewMedia(updatedNewMedia);
            publishLibrary(updatedExistingMedia);
          }
        } finally {
          setUploading(false);
        }
      }
    }
  };

  const handleDeleteMediaItem = async (itemid: number): Promise<void> => {
    if (!mediaSvcRef.current || deletingIdsRef.current.has(itemid)) return;
    const confirmed = await confirmation.requestConfirmation("Delete this file? <b>This action cannot be undone</b>", "Delete Media Item");
    if (!confirmed || deletingIdsRef.current.has(itemid)) return;

    deletingIdsRef.current.add(itemid);
    setDeletingIds(new Set(deletingIdsRef.current));
    try {
      const res = await mediaSvcRef.current.deleteMediaItem(itemid);
      if (folderIdRef.current !== folderId) return;
      if (res) {
        notifyLibraryRef.current = true;
        setExistingMedia(current => current.filter(file => file.Id !== itemid && file.SourceFileId !== itemid));
        setPreviewFile(prev => prev?.Id === itemid || prev?.SourceFileId === itemid ? null : prev);
      }
    } finally {
      deletingIdsRef.current.delete(itemid);
      setDeletingIds(new Set(deletingIdsRef.current));
    }
  };

  const openPreview = (file: IMediaItem): void => setPreviewFile(file);
  const closePreview = (): void => setPreviewFile(null);

  useEffect(() => {
    if (!previewFile || previewFile.FileType?.toLowerCase() !== "image" || !isPreviewableMedia(previewFile)) {
      setPreviewEditorUrl(null);
      setPreviewEditorError(null);
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;
    setPreviewEditorError(null);
    setPreviewEditorUrl(null);

    const loadEditorImage = async (): Promise<void> => {
      let blob: Blob;
      if (previewFile.FileData) {
        blob = previewFile.FileData;
      } else if (previewFile.Url?.startsWith("blob:") || previewFile.Url?.startsWith("data:")) {
        const response = await fetch(previewFile.Url);
        if (!response.ok) {
          throw new Error("The image could not be loaded for preview.");
        }
        blob = await response.blob();
      } else if (previewFile.Id) {
        blob = await (mediaSvcRef.current ?? new MediaService()).downloadFileContent(previewFile.Id);
      } else {
        throw new Error("The image could not be loaded for preview.");
      }

      objectUrl = URL.createObjectURL(blob);
      if (cancelled) {
        URL.revokeObjectURL(objectUrl);
        return;
      }

      setPreviewEditorUrl(objectUrl);
    };

    loadEditorImage().catch((error: unknown) => {
      if (!cancelled) {
        const message = error instanceof Error ? error.message : "The image could not be loaded for preview.";
        setPreviewEditorError(message);
      }
    });

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [previewFile]);
  const openVariantPreview = (file: IMediaItem): void => setVariantPreviewFile(file);
  const closeVariantPreview = (): void => setVariantPreviewFile(null);
  const clearUploads = (): void => setNewMedia([]);
  const removePreviewFile = (filename: string): void => setNewMedia((prev) => prev.filter((file) => file.Filename.toLowerCase() !== filename.toLowerCase()));

  const pendingItemFromFile = (file: File): IMediaItem => ({
    SiteId: "",
    Id: 0,
    Filename: file.name,
    DateAdded: new Date().toISOString(),
    AddedBy: "",
    Url: URL.createObjectURL(file),
    FileType: FileTypeHelper.getFileTypeFromMimeType(file.type, file.name),
    FileData: file,
    ContentType: file.type,
    FolderId: folderId,
    FolderName: "",
    FolderLevel: 0,
    SourceFileId: null,
    MetadataJson: null,
    SrcSet: [],
    IsVideo: false
  });

  const handleUseAiImage = (file: File): void => {
    setNewMedia((prev) => [...prev, pendingItemFromFile(file)]);
  };

  const handleReplaceAiImage = async (file: File, source: IMediaItem): Promise<boolean> => {
    if (!mediaSvcRef.current) {
      return false;
    }

    const confirmed = await confirmation.requestConfirmation(
      `Replace <b>${source.Filename}</b>? Pages using this address will show the new image.<br /><b>This action cannot be undone.</b>`,
      "Replace Media Item"
    );

    if (!confirmed) {
      return false;
    }

    try {
      const replaced = await mediaSvcRef.current.replaceFile(file, source.Id);
      if (!replaced) {
        return false;
      }

      if (folderIdRef.current !== folderId) {
        return true;
      }

      publishLibrary(existingMedia.map((item) => item.Id === replaced.Id ? replaced : item));
      setMediaRevisions((prev) => ({ ...prev, [replaced.Id]: Date.now() }));
      setPreviewFile((current) => current?.Id === replaced.Id ? replaced : current);
      return true;
    } catch {
      return false;
    }
  };

  const handleSaveAiAsNew = async (file: File): Promise<boolean> => {
    if (!mediaSvcRef.current) {
      return false;
    }

    try {
      const uploaded = await mediaSvcRef.current.uploadFile(file, folderId);
      if (!uploaded) {
        return false;
      }

      if (folderIdRef.current !== folderId) {
        return true;
      }

      const index = existingMedia.findIndex((item) => item.Id === uploaded.Id);
      publishLibrary(index === -1
        ? [...existingMedia, uploaded]
        : existingMedia.map((item) => item.Id === uploaded.Id ? uploaded : item));
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (!notifyLibraryRef.current) {
      return;
    }

    notifyLibraryRef.current = false;
    onLibraryChange?.(existingMedia);
  }, [existingMedia, onLibraryChange]);

  useEffect(() => {
    if (!mediaSvcRef.current) {
      mediaSvcRef.current = new MediaService();
    }
  }, []);

  useEffect(() => {
    const siteSvc = new SiteService();
    siteSvc.getSiteDetails().then((details) => setSiteDetails(details));
  }, []);

  const dropzoneClass = isDragAccept
    ? styles.dropzoneAccept
    : isDragReject
      ? styles.dropzoneReject
      : "";

  const renderExistingItem = (file: IMediaItem): JSX.Element => (
    <Col key={`FILE-${folderId}-${file.Id}`} sm={12} md={6} xl={4} xxl={3} className="mt-3">
      <Card className={styles.mediaCard} aria-busy={deletingIds.has(file.Id)} draggable={!deletingIds.has(file.Id)} onDragStart={(event) => mediaItemDragStart(event, file)}>
        <CardBody className={styles.mediaCardBody}>
          <div className={styles.mediaPreview} style={{ cursor: deletingIds.has(file.Id) ? "default" : "grab" }}>
            {deletingIds.has(file.Id)
              ? <div role="status" className="d-flex flex-column align-items-center gap-2 text-secondary"><Spinner animation="border" aria-hidden="true" /><span>Deleting {file.Filename}…</span></div>
              : renderPreview(file)}
          </div>
          <div className={styles.mediaName} title={file.Filename}>{file.Filename}</div>
          {getVariantFiles(file, existingMedia).length > 0 && (
            <div className="small text-secondary mt-2">
              {getVariantFiles(file, existingMedia).length} generated size{getVariantFiles(file, existingMedia).length === 1 ? "" : "s"}
            </div>
          )}
        </CardBody>
        <CardFooter className={styles.mediaCardFooter}>
          <fieldset disabled={deletingIds.has(file.Id)} className="m-0 p-0 border-0"><InputGroup className="justify-content-center" style={deletingIds.has(file.Id) ? { pointerEvents: "none", opacity: 0.5 } : undefined}>
            <InputGroupText className={styles.actionIcon} title="Copy file URL" onClick={() => handleCopyUrl(file)}>
              <FontAwesomeIcon icon={faCopy} className="text-secondary" />
            </InputGroupText>
            <InputGroupText className={styles.actionIcon} title="Download file" onClick={() => handleDownloadFile(file)}>
              <FontAwesomeIcon icon={faDownload} className="text-secondary" />
            </InputGroupText>
            <InputGroupText className={styles.actionIcon} title="Preview or edit file" onClick={() => openPreview(file)}>
              <FontAwesomeIcon icon={faMagnifyingGlass} className="text-secondary" />
            </InputGroupText>
            {getVariantFiles(file, existingMedia).length > 0 && (
              <InputGroupText className={styles.actionIcon} title="Show generated sizes" onClick={() => openVariantPreview(file)}>
                <FontAwesomeIcon icon={faImage} className="text-secondary" />
              </InputGroupText>
            )}
            {aiEditAvailable && file.FileType?.toLowerCase() === "image" && (
              <InputGroupText className={styles.actionIcon} title="Edit with AI" onClick={() => setAiDialog({ mode: "edit", source: file })}>
                <FontAwesomeIcon icon={faMagicWandSparkles} className="text-secondary" />
              </InputGroupText>
            )}
            <Button disabled={deletingIds.has(file.Id)} variant="outline-secondary" className={styles.actionIcon} title="Rename file" aria-label={`Rename ${file.Filename}`} onClick={() => setRenameFile(file)}>
              <FontAwesomeIcon icon={faPen} />
            </Button>
            <InputGroupText className={styles.actionIcon} title="Delete file" onClick={() => handleDeleteMediaItem(file.Id)}>
              <FontAwesomeIcon icon={faTrash} className="text-secondary" />
            </InputGroupText>
          </InputGroup></fieldset>
        </CardFooter>
      </Card>
    </Col>
  );

  const renderNewItem = (file: IMediaItem, index: number): JSX.Element => (
    <Col key={`NEW-${folderId}-${index}-${file.Filename}`} sm={12} md={6} xl={4} xxl={3} className="mt-3">
      <Card className={styles.mediaCard}>
        <CardBody className={styles.mediaCardBody}>
          <div className={styles.mediaPreview}>{renderPreview(file)}</div>
          <div className={styles.mediaName} title={file.Filename}>{file.Filename}</div>
        </CardBody>
        <CardFooter className={styles.mediaCardFooter}>
            <InputGroup className="justify-content-center">
            <InputGroupText className={styles.actionIcon} title="Upload this file" onClick={() => !uploading && handleUploadFile(file)}>
              {uploading && uploadingFileName === file.Filename
                ? <Spinner animation="border" size="sm" className="text-secondary" />
                : <FontAwesomeIcon icon={faUpload} className="text-secondary" />}
            </InputGroupText>
            <InputGroupText className={styles.actionIcon} title="Preview or edit file" onClick={() => openPreview(file)}>
              <FontAwesomeIcon icon={faMagnifyingGlass} className="text-secondary" />
            </InputGroupText>
            <InputGroupText className={styles.actionIcon} title="Remove file" onClick={() => removePreviewFile(file.Filename)}>
              <FontAwesomeIcon icon={faTrash} className="text-secondary" />
            </InputGroupText>
          </InputGroup>
        </CardFooter>
      </Card>
    </Col>
  );

  const previewEditorItem = useMemo(() => {
    if (!previewFile || previewFile.FileType?.toLowerCase() !== "image" || !isPreviewableMedia(previewFile)) {
      return null;
    }

    const url = previewEditorUrl || withRevision(resolveMediaUrl(previewFile.Url), previewFile.Id);
    if (!url) {
      return null;
    }

    return { ...previewFile, Url: url };
  }, [previewFile, previewEditorUrl]);

  return (
    <>
      <div id="DragImageIcon" style={{ visibility: "hidden" }}>
        <FontAwesomeIcon icon={faFileImage} />
      </div>

      {folderId > 0 && (
        <div id="galleryPreview" className={styles.previewShell}>
          <div {...getRootProps({ className: `${styles.dropzone} ${dropzoneClass}`.trim() })} style={{ cursor: "pointer" }}>
            <input {...getInputProps()} />
            <div className={styles.dropzoneInner}>
              <div className={styles.dropzoneTitle}>Drop files here or click to upload</div>
              <div className={styles.dropzoneText}>
                Add reusable media into {folderName} so editors can select it elsewhere in the CMS.
              </div>
            </div>
            {aiGenerateAvailable && (
              <div
                className={styles.dropzoneActions}
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}>
                <Button type="button" variant="outline-primary" size="sm" onClick={() => setAiDialog({ mode: "generate", source: null })}>
                  <FontAwesomeIcon icon={faMagicWandSparkles} className="me-2" />
                  Generate AI Image
                </Button>
              </div>
            )}
          </div>

          <Row className={styles.mediaGrid}>
            {loading && (
              <Col md={12} className="mt-3">
                <Loading Message={`Loading ${folderName}...`} SpinSize="2x" />
              </Col>
            )}

            {!loading && newMedia.length === 0 && (
              getOriginalFiles(existingMedia).length > 0
                ? getOriginalFiles(existingMedia).map((file) => renderExistingItem(file))
                : (
                  <Col md={12} className="mt-2">
                    <AppEmptyState
                      compact
                      title="Empty folder"
                      message={`No files have been uploaded to ${folderName} yet.`}
                      icon={faFolderOpen}
                    />
                  </Col>
                )
            )}

            {!loading && newMedia.length > 0 && (
              <>
                <Col md={12} className="mt-2">
                  <AppEmptyState
                    compact
                    title={`${newMedia.length} file${newMedia.length === 1 ? "" : "s"} ready to upload`}
                    message="Review the previews below, then upload everything together or clear the pending selection."
                    icon={faUpload}
                  />
                </Col>
                {newMedia.map((file, index) => renderNewItem(file, index))}
              </>
            )}
          </Row>

          {!loading && newMedia.length > 0 && (
            <div className={styles.uploadActions}>
              <Button variant="success" title="Upload all" onClick={handleUploadFiles} disabled={uploading}>
                {uploading && !uploadingFileName
                  ? <Spinner animation="border" size="sm" className="me-2" />
                  : <FontAwesomeIcon icon={faUpload} />}
                <span className="ms-2">{uploading && !uploadingFileName ? "Uploading..." : "Upload all"}</span>
              </Button>
              <Button variant="outline-secondary" title="Clear uploads" onClick={clearUploads} disabled={uploading}>
                Clear uploads
              </Button>
            </div>
          )}
          {uploading && (
            <div className="mt-3 text-secondary small">
              <Spinner animation="border" size="sm" className="me-2" />
              {uploadingFileName
                ? `Uploading ${uploadingFileName}...`
                : `Uploading ${newMedia.length} file${newMedia.length === 1 ? "" : "s"} to ${folderName}...`}
            </div>
          )}
        </div>
      )}

      {previewFile && (
        <Modal show={previewFile !== null} onHide={closePreview} size="xl" fullscreen={fullScreenEditor ? true : undefined}>
          <ModalHeader closeButton className="m-0 p-0 ps-3 pe-3 pt-1">
            <h5>{previewFile.Filename}</h5>
          </ModalHeader>
          <ModalBody style={{ height: "80vh" }}>
            <div className="h-100 d-flex flex-column">
              <div className="flex-grow-1 w-100 overflow-hidden" style={{ minHeight: 0 }}>
                {!isPreviewableMedia(previewFile)
                  ? <div className="h-100 d-flex align-items-center justify-content-center"><PreviewPlaceholder /></div>
                  : previewFile.FileType?.toLowerCase() !== "image"
                  ? <div className="p-3 h-100">{renderPreview(previewFile)}</div>
                  : previewEditorItem
                    ? <ImageEditor
                        mediaItem={previewEditorItem}
                        mimeType={FileTypeHelper.getImageMimeType(previewFile.Filename)}
                        onApplyChanges={() => undefined}
                        onSave={handleSaveEditedImage}
                        saveHint={previewFile.Id
                          ? "Save replaces this file and refreshes its generated sizes."
                          : "Save keeps this preview with the file waiting to upload."}
                        onFullScreenToggle={(fullscreen) => setFullScreenEditor(fullscreen)} />
                    : <div className="h-100 d-flex align-items-center justify-content-center text-danger">{previewEditorError || "The image could not be loaded for preview."}</div>
                }
              </div>

              {getVariantFiles(previewFile, existingMedia).length > 0 && (
                <div className="border-top pt-3 mt-3">
                  <div className="fw-semibold mb-2">Generated sizes</div>
                  <div className="small text-secondary mb-3">
                    These are managed from the original upload. Use the original for preview and delete; copy a variant URL if you need a specific size.
                  </div>
                  <div className={styles.sizeRow}>
                    {[...getVariantFiles(previewFile, existingMedia)]
                      .sort((left, right) => (variantWidth(left) ?? Number.MAX_SAFE_INTEGER) - (variantWidth(right) ?? Number.MAX_SAFE_INTEGER))
                      .map((variant) => (
                        <button
                          key={`SRCSET-${previewFile.Id}-${variant.Id}`}
                          type="button"
                          className={styles.sizeChip}
                          title={variant.Filename}
                          onClick={() => handleCopyUrl(variant)}
                        >
                          <span>{getVariantLabel(variant)}</span>
                          <FontAwesomeIcon icon={faCopy} />
                          <span className={styles.sizeChipAction}>Copy URL</span>
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </ModalBody>
        </Modal>
      )}

      {renameFile && (
        <RenameMediaDialog file={renameFile} onClose={() => setRenameFile(null)} onRename={async filename => {
          if (!mediaSvcRef.current) throw new Error("Media Manager is not ready. Please retry.");
          const renamed = await mediaSvcRef.current.renameFile(renameFile.Id, filename);
          if (folderIdRef.current === folderId) {
            notifyLibraryRef.current = true;
            setExistingMedia(current => current.map(item => item.Id === renamed.Id ? { ...item, ...renamed } : item));
            setPreviewFile(current => current?.Id === renamed.Id ? { ...current, ...renamed } : current);
            setRenameFile(null);
          }
        }} />
      )}

      {aiDialog && siteDetails && (
        <AiImageDialog
          mode={aiDialog.mode}
          site={siteDetails}
          folderId={folderId}
          sourceFile={aiDialog.source}
          onClose={() => setAiDialog(null)}
          onUseImage={handleUseAiImage}
          onReplace={(file) => aiDialog.source ? handleReplaceAiImage(file, aiDialog.source) : Promise.resolve(false)}
          onSaveAsNew={handleSaveAiAsNew}
        />
      )}

      {variantPreviewFile && (
        <Modal show={variantPreviewFile !== null} onHide={closeVariantPreview} size="lg">
          <ModalHeader closeButton className="m-0 p-0 ps-3 pe-3 pt-1">
            <h5>Generated sizes for {variantPreviewFile.Filename}</h5>
          </ModalHeader>
          <ModalBody className={styles.variantModalBody}>
            <div className={styles.variantModalIntro}>
              <div className={styles.variantModalTitle}>Responsive image variants</div>
              <div className={styles.variantModalText}>
                These sizes are generated automatically from the original upload. Use them when you need a specific breakpoint-ready URL without managing separate files manually.
              </div>
            </div>

            <Row className={`${styles.variantGrid} g-3`}>
              {getVariantFiles(variantPreviewFile, existingMedia).map((variant) => (
                <Col md={6} xl={4} key={`MODAL-SRCSET-${variantPreviewFile.Id}-${variant.Id}`}>
                  <Card className={`${styles.mediaCard} ${styles.variantCard}`}>
                    <CardBody className={styles.mediaCardBody}>
                      <div className={`${styles.mediaPreview} ${styles.variantPreview}`}>{renderPreview(variant)}</div>
                      <div className={styles.mediaName} title={variant.Filename}>{getVariantLabel(variant)}</div>
                    </CardBody>
                    <CardFooter className={styles.mediaCardFooter}>
                      <InputGroup className="justify-content-center">
                        <InputGroupText className={styles.actionIcon} title="Copy file URL" onClick={() => handleCopyUrl(variant)}>
                          <FontAwesomeIcon icon={faCopy} className="text-secondary" />
                        </InputGroupText>
                      </InputGroup>
                    </CardFooter>
                  </Card>
                </Col>
              ))}
            </Row>
          </ModalBody>
        </Modal>
      )}
    </>
  );
});

export default MediaUploadPreview;
